import { useState, useMemo, useEffect } from "react";
import {
  Mail,
  Phone,
  MessageSquare,
  Video,
  Users,
  FileText,
  Search,
  Eye,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Label } from "@/components/ui/label";
import { formatDate } from "@/lib/utils";
import { getCustomerCommunications } from "@/lib/api/customer-detail";
import { mapCommunicationApiToUi } from "@/lib/mappers/customer-detail";
import { getCommunicationsByCustomerId } from "@/lib/demo-data/customer-communications-contacts";
import { toast } from "sonner";

interface Communication {
  id: string;
  customerId: string;
  customerName: string;
  contactName: string;
  type: "email" | "phone" | "chat" | "meeting" | "sms" | "video" | "note" | "portal";
  subject: string;
  content: string;
  status: "sent" | "delivered" | "read" | "replied" | "completed" | "failed";
  direction: "inbound" | "outbound";
  createdAt: string;
  createdBy: string;
  metadata?: {
    emailTo?: string;
    emailFrom?: string;
    duration?: string;
    attendees?: string[];
  };
}

interface CommunicationHistoryTabProps {
  customerId: string;
  customerName: string;
}

// Mock communications data - exported for potential use in other components
export const allCommunications: Communication[] = [
  {
    id: "COMM-001",
    customerId: "1",
    customerName: "Acme Corporation",
    contactName: "John Smith",
    type: "email",
    subject: "Payment Reminder - Invoice INV-001",
    content: "Dear John,\n\nThis is a friendly reminder regarding Invoice INV-001...",
    status: "read",
    direction: "outbound",
    createdAt: "2024-03-10T14:30:00Z",
    createdBy: "Sarah Johnson",
    metadata: {
      emailTo: "john.smith@acmecorp.com",
      emailFrom: "ar@company.com",
    },
  },
  {
    id: "COMM-002",
    customerId: "1",
    customerName: "Acme Corporation",
    contactName: "John Smith",
    type: "phone",
    subject: "Follow-up on overdue payment",
    content: "Discussed payment plan for outstanding invoices. Customer agreed to pay 50% by end of week.",
    status: "completed",
    direction: "outbound",
    createdAt: "2024-03-08T10:15:00Z",
    createdBy: "Michael Brown",
    metadata: {
      duration: "15 minutes",
    },
  },
  {
    id: "COMM-003",
    customerId: "2",
    customerName: "Global Tech Industries",
    contactName: "Michael Chen",
    type: "email",
    subject: "Account Statement - February 2024",
    content: "Please find attached your account statement for February 2024...",
    status: "delivered",
    direction: "outbound",
    createdAt: "2024-03-05T09:00:00Z",
    createdBy: "Sarah Johnson",
    metadata: {
      emailTo: "michael.chen@globaltech.com",
      emailFrom: "ar@company.com",
    },
  },
  {
    id: "COMM-004",
    customerId: "2",
    customerName: "Global Tech Industries",
    contactName: "Michael Chen",
    type: "chat",
    subject: "Quick question about invoice",
    content: "Customer inquired about invoice date discrepancy. Resolved immediately.",
    status: "completed",
    direction: "inbound",
    createdAt: "2024-03-03T16:45:00Z",
    createdBy: "System",
  },
  {
    id: "COMM-005",
    customerId: "3",
    customerName: "Mega Retail Group",
    contactName: "Emily Davis",
    type: "meeting",
    subject: "Quarterly Account Review",
    content: "Reviewed account performance, discussed payment terms adjustment.",
    status: "completed",
    direction: "outbound",
    createdAt: "2024-03-01T14:00:00Z",
    createdBy: "David Wilson",
    metadata: {
      duration: "45 minutes",
      attendees: ["Emily Davis", "David Wilson", "Sarah Johnson"],
    },
  },
  {
    id: "COMM-006",
    customerId: "1",
    customerName: "Acme Corporation",
    contactName: "Sarah Johnson",
    type: "email",
    subject: "Payment confirmation received",
    content: "Thank you for your payment of $50,000. Payment has been applied to your account.",
    status: "sent",
    direction: "outbound",
    createdAt: "2024-02-28T11:20:00Z",
    createdBy: "Sarah Johnson",
    metadata: {
      emailTo: "sarah.johnson@acmecorp.com",
      emailFrom: "ar@company.com",
    },
  },
  {
    id: "COMM-007",
    customerId: "4",
    customerName: "Premier Manufacturing",
    contactName: "Robert Williams",
    type: "sms",
    subject: "Payment reminder",
    content: "Reminder: Invoice INV-045 is due tomorrow. Please arrange payment.",
    status: "delivered",
    direction: "outbound",
    createdAt: "2024-02-25T08:00:00Z",
    createdBy: "System",
  },
  {
    id: "COMM-008",
    customerId: "3",
    customerName: "Mega Retail Group",
    contactName: "Emily Davis",
    type: "video",
    subject: "Payment plan discussion",
    content: "Video call to discuss restructuring payment plan for Q1 invoices.",
    status: "completed",
    direction: "outbound",
    createdAt: "2024-02-20T15:30:00Z",
    createdBy: "Michael Brown",
    metadata: {
      duration: "30 minutes",
    },
  },
  {
    id: "COMM-009",
    customerId: "5",
    customerName: "Sunrise Enterprises",
    contactName: "Jennifer Martinez",
    type: "note",
    subject: "Internal note - customer situation",
    content: "Customer experiencing temporary cash flow issues. Agreed to payment extension.",
    status: "completed",
    direction: "outbound",
    createdAt: "2024-02-15T13:00:00Z",
    createdBy: "David Wilson",
  },
  {
    id: "COMM-010",
    customerId: "1",
    customerName: "Acme Corporation",
    contactName: "John Smith",
    type: "phone",
    subject: "Dispute resolution",
    content: "Customer raised dispute about invoice amount. Investigating and will follow up.",
    status: "completed",
    direction: "inbound",
    createdAt: "2024-02-10T11:30:00Z",
    createdBy: "Sarah Johnson",
    metadata: {
      duration: "20 minutes",
    },
  },
  {
    id: "COMM-011",
    customerId: "2",
    customerName: "Global Tech Industries",
    contactName: "Michael Chen",
    type: "email",
    subject: "Payment plan proposal",
    content: "Proposed payment plan for outstanding balance. Awaiting customer response.",
    status: "sent",
    direction: "outbound",
    createdAt: "2024-02-05T10:00:00Z",
    createdBy: "Michael Brown",
    metadata: {
      emailTo: "michael.chen@globaltech.com",
      emailFrom: "ar@company.com",
    },
  },
  {
    id: "COMM-012",
    customerId: "4",
    customerName: "Premier Manufacturing",
    contactName: "Robert Williams",
    type: "email",
    subject: "Invoice delivery confirmation",
    content: "Confirming receipt of invoices INV-045, INV-046, INV-047.",
    status: "replied",
    direction: "inbound",
    createdAt: "2024-02-01T09:15:00Z",
    createdBy: "System",
    metadata: {
      emailFrom: "robert.williams@premier.com",
      emailTo: "ar@company.com",
    },
  },
];

export function CommunicationHistoryTab({
  customerId,
  customerName,
}: CommunicationHistoryTabProps) {
  // Initialize with mock data immediately for instant display
  const initialCommunications = useMemo(() => {
    const mockCommunications = getCommunicationsByCustomerId(customerId);
    return mockCommunications.map(mapCommunicationApiToUi) as Communication[];
  }, [customerId]);

  const [communications, setCommunications] = useState<Communication[]>(initialCommunications);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [directionFilter, setDirectionFilter] = useState("all");
  const [dateRangeFilter, setDateRangeFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [viewDetailsDialog, setViewDetailsDialog] = useState(false);
  const [selectedCommunication, setSelectedCommunication] = useState<Communication | null>(null);

  const itemsPerPage = 10;

  useEffect(() => {
    const loadCommunications = async () => {
      try {
        const response = await getCustomerCommunications(customerId);
        const uiCommunications = response.communications.map(mapCommunicationApiToUi);
        setCommunications(uiCommunications);
      } catch (error) {
        console.error("Failed to load communications:", error);
        toast.error("Failed to load communications");
      }
    };

    loadCommunications();
  }, [customerId]);

  // Filter communications for this customer
  const customerCommunications = communications;

  // Apply filters
  const filteredCommunications = useMemo(() => {
    return customerCommunications.filter((comm) => {
      const matchesSearch =
        comm.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
        comm.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
        comm.contactName.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesType = typeFilter === "all" || comm.type === typeFilter;
      const matchesStatus = statusFilter === "all" || comm.status === statusFilter;
      const matchesDirection = directionFilter === "all" || comm.direction === directionFilter;

      // Date range filter
      let matchesDateRange = true;
      if (dateRangeFilter !== "all") {
        const commDate = new Date(comm.createdAt);
        const today = new Date();
        const daysDiff = Math.floor(
          (today.getTime() - commDate.getTime()) / (1000 * 60 * 60 * 24)
        );

        switch (dateRangeFilter) {
          case "7days":
            matchesDateRange = daysDiff <= 7;
            break;
          case "30days":
            matchesDateRange = daysDiff <= 30;
            break;
          case "90days":
            matchesDateRange = daysDiff <= 90;
            break;
        }
      }

      return matchesSearch && matchesType && matchesStatus && matchesDirection && matchesDateRange;
    });
  }, [customerCommunications, searchQuery, typeFilter, statusFilter, directionFilter, dateRangeFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredCommunications.length / itemsPerPage);
  const paginatedCommunications = filteredCommunications.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Get icon for communication type
  const getTypeIcon = (type: string) => {
    switch (type) {
      case "email":
        return <Mail className="h-4 w-4" />;
      case "phone":
        return <Phone className="h-4 w-4" />;
      case "chat":
        return <MessageSquare className="h-4 w-4" />;
      case "meeting":
        return <Users className="h-4 w-4" />;
      case "sms":
        return <MessageSquare className="h-4 w-4" />;
      case "video":
        return <Video className="h-4 w-4" />;
      case "note":
        return <FileText className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  // Get badge color for type
  const getTypeBadgeColor = (type: string) => {
    switch (type) {
      case "email":
        return "bg-blue-500 text-white hover:bg-blue-600";
      case "phone":
        return "bg-green-500 text-white hover:bg-green-600";
      case "chat":
        return "bg-purple-500 text-white hover:bg-purple-600";
      case "meeting":
        return "bg-orange-500 text-white hover:bg-orange-600";
      case "sms":
        return "bg-cyan-500 text-white hover:bg-cyan-600";
      case "video":
        return "bg-pink-500 text-white hover:bg-pink-600";
      case "note":
        return "bg-gray-500 text-white hover:bg-gray-600";
      default:
        return "bg-gray-500 text-white hover:bg-gray-600";
    }
  };

  // Get status badge variant
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "sent":
      case "delivered":
        return "secondary";
      case "read":
      case "replied":
      case "completed":
        return "default";
      case "failed":
        return "destructive";
      default:
        return "secondary";
    }
  };

  // Handle view details
  const handleViewDetails = (communication: Communication) => {
    setSelectedCommunication(communication);
    setViewDetailsDialog(true);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Communication History for {customerName}</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="mb-4 space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search communications..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Filter dropdowns */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="phone">Phone</SelectItem>
                  <SelectItem value="chat">Chat</SelectItem>
                  <SelectItem value="meeting">Meeting</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="video">Video</SelectItem>
                  <SelectItem value="note">Note</SelectItem>
                </SelectContent>
              </Select>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="read">Read</SelectItem>
                  <SelectItem value="replied">Replied</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>

              <Select value={directionFilter} onValueChange={setDirectionFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Direction" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Directions</SelectItem>
                  <SelectItem value="inbound">Inbound</SelectItem>
                  <SelectItem value="outbound">Outbound</SelectItem>
                </SelectContent>
              </Select>

              <Select value={dateRangeFilter} onValueChange={setDateRangeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Date Range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="7days">Last 7 Days</SelectItem>
                  <SelectItem value="30days">Last 30 Days</SelectItem>
                  <SelectItem value="90days">Last 90 Days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Direction</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Created By</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedCommunications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground">
                      No communications found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedCommunications.map((comm) => (
                    <TableRow key={comm.id}>
                      <TableCell>
                        <Badge className={getTypeBadgeColor(comm.type)}>
                          <span className="mr-1">{getTypeIcon(comm.type)}</span>
                          {comm.type}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">{comm.subject}</TableCell>
                      <TableCell>{comm.contactName}</TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(comm.status)}>
                          {comm.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={comm.direction === "inbound" ? "outline" : "secondary"}>
                          {comm.direction}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(comm.createdAt)}</TableCell>
                      <TableCell>{comm.createdBy}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(comm)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, filteredCommunications.length)} of{" "}
                {filteredCommunications.length} communications
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Details Dialog */}
      <Dialog open={viewDetailsDialog} onOpenChange={setViewDetailsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Communication Details</DialogTitle>
            <DialogDescription>{selectedCommunication?.id}</DialogDescription>
          </DialogHeader>
          {selectedCommunication && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Type</Label>
                  <div className="mt-1">
                    <Badge className={getTypeBadgeColor(selectedCommunication.type)}>
                      <span className="mr-1">{getTypeIcon(selectedCommunication.type)}</span>
                      {selectedCommunication.type}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <div className="mt-1">
                    <Badge variant={getStatusBadgeVariant(selectedCommunication.status)}>
                      {selectedCommunication.status}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Direction</Label>
                  <div className="mt-1">
                    <Badge
                      variant={
                        selectedCommunication.direction === "inbound" ? "outline" : "secondary"
                      }
                    >
                      {selectedCommunication.direction}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Date</Label>
                  <p className="mt-1 font-medium">
                    {formatDate(selectedCommunication.createdAt)}
                  </p>
                </div>
              </div>

              <div>
                <Label className="text-muted-foreground">Customer</Label>
                <p className="mt-1 font-medium">{selectedCommunication.customerName}</p>
              </div>

              <div>
                <Label className="text-muted-foreground">Contact</Label>
                <p className="mt-1 font-medium">{selectedCommunication.contactName}</p>
              </div>

              <div>
                <Label className="text-muted-foreground">Created By</Label>
                <p className="mt-1 font-medium">{selectedCommunication.createdBy}</p>
              </div>

              <div>
                <Label className="text-muted-foreground">Subject</Label>
                <p className="mt-1 font-medium">{selectedCommunication.subject}</p>
              </div>

              <div>
                <Label className="text-muted-foreground">Content</Label>
                <div className="mt-1 rounded-md border bg-muted/50 p-4">
                  <p className="whitespace-pre-wrap">{selectedCommunication.content}</p>
                </div>
              </div>

              {selectedCommunication.metadata && (
                <div>
                  <Label className="text-muted-foreground">Additional Information</Label>
                  <div className="mt-1 space-y-2 rounded-md border bg-muted/50 p-4">
                    {selectedCommunication.metadata.emailTo && (
                      <div>
                        <span className="font-medium">To:</span>{" "}
                        {selectedCommunication.metadata.emailTo}
                      </div>
                    )}
                    {selectedCommunication.metadata.emailFrom && (
                      <div>
                        <span className="font-medium">From:</span>{" "}
                        {selectedCommunication.metadata.emailFrom}
                      </div>
                    )}
                    {selectedCommunication.metadata.duration && (
                      <div>
                        <span className="font-medium">Duration:</span>{" "}
                        {selectedCommunication.metadata.duration}
                      </div>
                    )}
                    {selectedCommunication.metadata.attendees && (
                      <div>
                        <span className="font-medium">Attendees:</span>{" "}
                        {selectedCommunication.metadata.attendees.join(", ")}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
